import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ca-theme';
const SCHEME_QUERY = '(prefers-color-scheme: light)';
/** Keeps every useClientTheme instance in the tab in step when one of them changes the preference */
const CHANGE_EVENT = 'ca-theme-change';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'auto' || value === 'light' || value === 'dark';

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

function systemTheme(): ResolvedTheme {
  try {
    return window.matchMedia(SCHEME_QUERY).matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Client app theme: follows `prefers-color-scheme` unless the viewer picked Claro / Oscuro, stored per viewer in
 * localStorage. Apply `resolved` as `data-theme` on the `.client-app` root.
 */
export function useClientTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const media = window.matchMedia(SCHEME_QUERY);
    const onScheme = () => setSystem(media.matches ? 'light' : 'dark');
    const onStorage = () => setPreferenceState(readPreference());
    const onChange = (event: Event) => setPreferenceState((event as CustomEvent<ThemePreference>).detail);
    media.addEventListener?.('change', onScheme);
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      media.removeEventListener?.('change', onScheme);
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      if (next === 'auto') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage can be blocked (private window); the choice then lasts until reload
    }
    setPreferenceState(next);
    window.dispatchEvent(new CustomEvent<ThemePreference>(CHANGE_EVENT, { detail: next }));
  }, []);

  const resolved: ResolvedTheme = preference === 'auto' ? system : preference;
  return { preference, resolved, setPreference };
}
